import { bookActions } from '@/actions';
import { CheckoutDetailsProps } from '@/components/modules/checkout/CheckoutDetails/CheckoutDetails';
import {
  CheckoutDetailsGiftcard,
  CheckoutDetailsService,
  CheckoutDetailsBundle,
  CheckoutDetailsProduct,
} from '@/types/checkout';

import { CAR_INDUSTRIES, HAIRDRESSER_INDUSTRIES } from '@/constants/analytics';
import { CHECKOUT_PAYMENT_METHOD } from '@/constants/checkout';
import { isGroupBooking as checkIfGroupBooking } from '@/helpers';
import { _s } from '@/locale';
import { bookServices as oldBookServices } from '@/services';
import { CheckoutTracking } from '@/types/analytics/checkout';
import { ConfirmedBooking } from '@/types/api/services/booking';
import { Employee } from '@/types/api/services/booking/schema';
import { ClientBundle } from '@/types/api/services/users/schema';
import { SelectedPaymentMethod } from '@/types/checkout';
import { Giftcards } from '@/types/state/book';
import moment from 'moment';
import {
  amplitudeLogRevenue,
  analytics4TrackRevenue,
  facebookTrackRevenue,
  salonTrackEventPurchase,
  trackEventTikTok,
  trackMpAnalyticsEvent,
  trackMpMetaEvent,
  trackMpPinterestEvent,
  trackMpQuantcastEvent,
  trackPageCio,
} from './analytics';
import { getGiftcardTypeLabel, getSubtotalLabel } from './checkout';
import { getHoursInterval, makeReadableDayShort } from './date';
import { isEmpty } from './objects';
import {
  getDynamicPriceListIdKey,
  getPlaceTimezone,
  getServiceCampaignIfAny,
  getServiceDuration,
  getServicePrice,
  getTotalPrice,
  shouldShowFrom,
} from './placeHelper';
import { serviceIsExtraTimeAddOn, servicesWithExtraTimeAddOns } from './service';

export function getBookingEmployee(booking: ConfirmedBooking, fromBookAgain: boolean = false): Employee {
  const anyEmployee = Boolean(booking?.extra?.anyEmployee);
  const anyEmployeeSelected = fromBookAgain && Boolean(booking?.extra?.anyEmployeeSelected);

  return anyEmployee || anyEmployeeSelected
    ? { id: 0, about: { name: _s('anyEmployee') } }
    : booking.services[0]?.employee || { id: 0, about: { name: _s('anyEmployee') } };
}

function getBookingDateTime(booking: ConfirmedBooking): { date: string; time: string } {
  const timezone = getPlaceTimezone(booking.place);
  const date = makeReadableDayShort(booking.start * 1000, timezone, false);
  const time = getHoursInterval(booking.start * 1000, 0, timezone);
  return {
    date,
    time,
  };
}

export function mapConfirmedBookingInfoGiftcards(
  booking: ConfirmedBooking,
  _addedGiftcards?: Giftcards,
  onRemoveGiftcard?: (code: string) => () => void,
): CheckoutDetailsGiftcard[] {
  const existingGiftcards: CheckoutDetailsGiftcard[] = booking.extra?.giftcards?.map((giftcard) => {
    const { code, amount } = giftcard;
    return {
      code,
      amount: `- ${amount / 100} kr`,
      label: getGiftcardTypeLabel(giftcard),
      expiryDate: moment(giftcard.giftCardExpiryDate).format('YYYY-MM-DD'),
    };
  });

  const addedGiftcards: CheckoutDetailsGiftcard[] = Object.values(_addedGiftcards ?? {}).map((giftcard) => {
    const { code, amount } = giftcard;
    return {
      code,
      amount: `-${amount / 100} kr`,
      label: getGiftcardTypeLabel(giftcard),
      expiryDate: moment(giftcard.giftCardExpiryDate).format('YYYY-MM-DD'),
      ...(onRemoveGiftcard ? { onRemove: onRemoveGiftcard(code) } : {}),
    };
  });

  const totalGiftcards = [...(existingGiftcards ?? []), ...(addedGiftcards ?? [])];

  return totalGiftcards;
}

export function mapConfirmedBookingInfoProducts(booking: ConfirmedBooking): CheckoutDetailsProduct[] {
  const campaignProducts: CheckoutDetailsProduct[] = [];

  for (const campaign of booking.extra?.campaigns ?? []) {
    for (const product of campaign.products ?? []) {
      campaignProducts.push({
        name: product.name,
        price: product.originalPriceLabel ? product.originalPriceLabel : product.priceLabel,
        discount: product.originalPriceLabel ? product.priceLabel : undefined,
      });
    }
  }

  return campaignProducts;
}

export function mapConfirmedBookingInfoServices(
  booking: ConfirmedBooking,
  hideBundleUsages?: boolean,
): CheckoutDetailsService[] {
  const { place, extra } = booking;
  const { campaigns, appliedBundle } = extra;

  const employee = getBookingEmployee(booking);
  const { groupBooking, bookingTitle } = checkIfGroupBooking(booking, true, true);
  const isGroupBooking = groupBooking > 0;

  const priceListId = employee && employee.about && employee.about.priceListId ? employee.about.priceListId : 0;

  const { mapServiceWithExtraTimeAddOns } = servicesWithExtraTimeAddOns(
    booking?.services?.map((s) => s?.service).filter((a) => !!a) ?? [],
  );

  const serviceRows = booking?.services?.map((service, index) => {
    if (isGroupBooking && index > 0) return undefined;

    if (serviceIsExtraTimeAddOn(service.service)) return undefined;

    const extraTimeAddOn = mapServiceWithExtraTimeAddOns[service.service.id];
    const duration = getServiceDuration(service.service, priceListId, 1, extraTimeAddOn);
    const name = isGroupBooking ? bookingTitle : service.service.name;
    const dynamicPriceListId = getDynamicPriceListIdKey(booking);
    if (appliedBundle && appliedBundle.service?.serviceId === service.service.id) {
      return {
        name,
        price: '0 kr',
        duration: hideBundleUsages
          ? duration
          : _s('bundleCheckout.usageCount', {
              usages: appliedBundle.service.usages,
              quantity: appliedBundle.service.quantity,
            }),
      };
    }

    let price: string, offerPrice: string;
    if (extraTimeAddOn?.id) {
      /* Sending service instead of service.service for correct overwritePrice */
      price = `${getTotalPrice(
        [service, extraTimeAddOn],
        place,
        priceListId,
        {},
        0,
        1,
        true,
        [],
        false,
        dynamicPriceListId,
      )}`;

      offerPrice = `${getTotalPrice(
        [service, extraTimeAddOn],
        place,
        priceListId,
        {},
        0,
        1,
        true,
        campaigns,
        false,
        getDynamicPriceListIdKey(booking),
      )}`;
    } else {
      const overwritePrice =
        service.extra?.overwritePrice && !isNaN(Number(service.extra.overwritePrice))
          ? Number(service.extra.overwritePrice)
          : undefined;

      const { campaignService } = getServiceCampaignIfAny(service.service, campaigns);

      price = getServicePrice(service.service, priceListId, place, null, dynamicPriceListId, overwritePrice);
      offerPrice = getServicePrice(
        service.service,
        priceListId,
        place,
        campaignService,
        dynamicPriceListId,
        overwritePrice,
      );
    }

    const discount = (() => {
      if (offerPrice === price) return null;
      return offerPrice;
    })();

    return { name, price, duration, discount: discount ? discount : null };
  });

  return serviceRows.filter(Boolean);
}

export function mapConfirmedBookingToCheckoutDetailsProps({
  booking,
  selectedPaymentMethod,
  addedGiftcards,
}: {
  booking: ConfirmedBooking;
  selectedPaymentMethod?: SelectedPaymentMethod;
  addedGiftcards?: Giftcards;
}): CheckoutDetailsProps {
  const { place, extra, appliedBundle, platformFee } = booking;
  const { campaigns, giftcards } = extra || {};
  const { date, time } = getBookingDateTime(booking);
  const totalGiftcards = { ...giftcards, ...(addedGiftcards || {}) };
  const onlineFee = extra.onHoldOnlineFee ?? extra.onlineFee ?? platformFee ?? selectedPaymentMethod?.onlineFee ?? 0;
  const products = (campaigns || []).flatMap((campaign) => campaign.products || []);

  const placeName = place.about.name;
  const information = '';
  const logo = place.about?.profileImage
    ? place.about.profileImage
    : place.about?.images?.length > 0
    ? place.about?.images?.[0]
    : null;
  const dateTime = `${date} ${_s('at')} ${time}`;

  const employee = getBookingEmployee(booking);

  const total = getTotalPrice(
    booking.services,
    place,
    employee.about?.priceListId,
    totalGiftcards || {},
    0,
    1,
    true,
    campaigns || [],
    false,
    getDynamicPriceListIdKey(booking),
    appliedBundle,
    onlineFee,
    products,
  );

  const paymentMethod = selectedPaymentMethod ?? {
    type: booking.extra.paymentMethod,
    ...(booking.extra.paymentMethod === 4 && {
      brand: booking.extra?.onlinePayment?.method ?? 'unknown',
      lastFour: booking.extra.onlinePayment?.lastFour ?? '',
      id: '',
    }),
  };

  const isFromPrice = shouldShowFrom(booking.services, place);
  const isPriceVariable = isFromPrice && total !== _s('variablePrice') && total !== _s('freePrice');
  const totalLabel = isPriceVariable ? _s('from') : _s('total');
  const priceToPay = { label: totalLabel, price: `${total}` };

  /**
   * We do not want to show payment method section if the only service in the booking is paid with bundle
   */
  const showPaymentMethod = (() => {
    if (!appliedBundle) return true;
    const bundleBookingIncludesAddons = booking.services.some(
      (service) => service.service.id !== appliedBundle.service.serviceId,
    );
    return bundleBookingIncludesAddons;
  })();

  const fees: CheckoutDetailsProps['fees'] = [];
  let subtotal: CheckoutDetailsProps['subtotal'];

  if (onlineFee > 0) {
    fees.push({ type: 'onlinePayment', amount: `${onlineFee} kr` });
    const totalIncludingFee = +getTotalPrice(
      booking.services,
      place,
      employee.about?.priceListId,
      totalGiftcards || {},
      0,
      1,
      false,
      campaigns || [],
      false,
      getDynamicPriceListIdKey(booking),
      appliedBundle,
      onlineFee,
      products,
    );
    subtotal = getSubtotalLabel(totalIncludingFee, onlineFee);
  }

  return {
    dateTime,
    information,
    logo,
    performer: employee.about.name,
    performerAvatar: employee.about?.avatar || '',
    placeName,
    priceToPay,
    paymentMethod: showPaymentMethod ? paymentMethod : undefined,
    fees,
    subtotal,
  };
}

export function mapConfirmedBundleToCheckoutDetailsProps({
  bundle,
  selectedPaymentMethod,
}: {
  bundle: ClientBundle;
  selectedPaymentMethod?: SelectedPaymentMethod;
}): CheckoutDetailsProps {
  const totalLabel = _s('total');
  const priceToPay = { label: totalLabel, price: bundle.finalPriceLabel ?? bundle.priceLabel };

  const paymentMethod = ((): SelectedPaymentMethod => {
    if (selectedPaymentMethod) return selectedPaymentMethod;

    switch (bundle.paymentMethod.type) {
      case 'Qliro':
        return { type: CHECKOUT_PAYMENT_METHOD.QLIRO };
      case 'Swish':
        return { type: CHECKOUT_PAYMENT_METHOD.SWISH };
      case 'ApplePay':
        return { type: CHECKOUT_PAYMENT_METHOD.APPLE_PAY };
      case 'GooglePay':
        return { type: CHECKOUT_PAYMENT_METHOD.GOOGLE_PAY };
      case 'StoredCard':
        return {
          type: CHECKOUT_PAYMENT_METHOD.STORED_COF,
          brand: bundle.paymentMethod?.cardDetails?.brand?.id ?? 'unknown',
          lastFour: bundle.paymentMethod.cardDetails?.lastFour ?? '',
          id: '',
        };
      case 'Klarna':
        return { type: CHECKOUT_PAYMENT_METHOD.KLARNA };
      case 'Walley':
        return { type: CHECKOUT_PAYMENT_METHOD.WALLEY };
    }
  })();

  return {
    logo: bundle.place.about.profileImage,
    performer: _s('anyEmployee'),
    placeName: bundle.place.about.name,
    bundles: mapConfirmedBundleToCheckoutDetailsBundles(bundle),
    paymentMethod,
    priceToPay,
    information: '',
    dateTime: '',
  };
}

function mapConfirmedBundleToCheckoutDetailsBundles(bundle: ClientBundle): CheckoutDetailsBundle[] {
  const bundles = ((): CheckoutDetailsBundle[] => {
    return [
      {
        name: bundle.name,
        price: `${bundle.priceLabel}`,
        discount: bundle.finalPriceLabel,
        quantity: `(${_s('buyBundles.quantity', { count: bundle.service.quantity })})`,
        validity: _s('buyBundles.valid', { validity: _s('months', { count: bundle.validMonths }) }),
      },
    ];
  })();

  return bundles;
}

async function getBookingCategory(booking: ConfirmedBooking) {
  try {
    const { services } = booking;
    if (services && services[0] && services[0]?.service?.about?.product) {
      const response = await oldBookServices.getBookingCategory(services[0].service.about.product);
      const { product } = response || {};
      if (product && product.productFamily) {
        return product.productFamily;
      }
    }
    return undefined;
  } catch (e) {
    return undefined;
  }
}

export async function getBookingTrackingProps(booking: ConfirmedBooking): Promise<CheckoutTracking.Booking> {
  const { id: bookingId } = booking;
  const { transactionProps, ...bookingTrackingProps } = bookActions.getTrackingProps() || {};

  if (isEmpty(bookingTrackingProps)) return null;

  if (bookingId) {
    bookingTrackingProps['booking_id'] = bookingId;
    transactionProps.orderId = bookingId;
  }

  bookingTrackingProps['service_category'] = await getBookingCategory(booking);
  return { transactionProps, ...bookingTrackingProps };
}

const MEETING_BOOKED_PLACE_IDS = [136523, 134407, 136566, 134906, 134519];

export async function sendBookingConfirmationSuccessEvent(_bookingTrackingProps: CheckoutTracking.Booking) {
  const { transactionProps, ...bookingTrackingProps } = _bookingTrackingProps;
  const { booking_id } = bookingTrackingProps;
  const isSistaminutenMarketplaceBooking = transactionProps?.isSistaminutentiderBooking;
  const isDealBooking = transactionProps?.isDealBooking;
  const bookingValue = Number(transactionProps?.value ?? transactionProps?.hiddenValue ?? 0);

  trackMpAnalyticsEvent(
    `booking_success${isSistaminutenMarketplaceBooking ? '_sistaminutentider' : isDealBooking ? '_deals' : ''}`,
    {
      booking_source: 'marketplace',
      value: bookingValue,
      currency: transactionProps?.currency,
      transaction_id: booking_id,
    },
  );

  amplitudeLogRevenue({ ...transactionProps });

  if (transactionProps?.place?.id && MEETING_BOOKED_PLACE_IDS.includes(transactionProps.place.id)) {
    trackPageCio('meeting_booked', {
      merchant_id: transactionProps?.place?.id,
      merchant_name: transactionProps?.place?.name,
    });
  }

  if (transactionProps?.allowMarketing) {
    const bookingType = isSistaminutenMarketplaceBooking
      ? 'booking_sistaminutentider'
      : isDealBooking
      ? 'booking_deals'
      : 'booking';

    let quantcastLabels = [`_fp.event.Purchase_${bookingType}`];
    if (isSistaminutenMarketplaceBooking) {
      quantcastLabels.push('_fp.event.PurchaseConfirmation');
    }

    facebookTrackRevenue({ ...transactionProps });

    analytics4TrackRevenue({ ...transactionProps });

    const placeAddress = ((): string => {
      const city = transactionProps?.place?.contact?.address?.city
        ? transactionProps.place.contact.address.city.trim()
        : '';
      const street = transactionProps?.place?.contact?.address?.street
        ? transactionProps.place?.contact.address.street.trim()
        : '';
      return `${street}${street && city ? ', ' : ''}${city}`;
    })();

    const cioData: any = {
      booking_id,
      merchant_id: transactionProps?.place?.id,
      merchant_name: transactionProps?.place?.name,
      merchant_city: transactionProps?.place?.contact?.address?.city,
      merchant_address: placeAddress,
      service_name: transactionProps?.contentName,
      service_category: bookingTrackingProps?.service_category,
    };
    if (isSistaminutenMarketplaceBooking) {
      cioData.is_sistaminutentider_marketplace_booking = true;
      cioData.is_sistaminuten = true;
    }
    if (isDealBooking) {
      cioData.is_deals_booking = true;
    }
    trackPageCio('booking_confirmation', cioData);

    trackEventTikTok('BookingConfirmed', {
      value: bookingValue, // number. Value of the order or items sold. Example: 100.
      currency: transactionProps?.currency, // string. The 4217 currency code. Example: 'USD'.
      contents: (transactionProps?.items || []).map((item) => ({
        content_id: item.id, // string. ID of the product. Example: '1077218'.
        content_type: 'product', // string. Either product or product_group.
        content_name: item.name, // string. The name of the page or product. Example: 'shirt'.
      })),
      content_type: 'product',
      content_category: bookingType,
      description: transactionProps?.description, // string. The description of the item or page. Example: "light weight cotton".
    });

    trackMpPinterestEvent('BookingConfirmed', {
      value: bookingValue,
      currency: transactionProps?.currency,
      property: bookingType,
    });

    trackMpQuantcastEvent(quantcastLabels, {
      revenue: bookingValue,
      currency: transactionProps?.currency,
    });

    if (isSistaminutenMarketplaceBooking) {
      trackEventTikTok('PlaceAnOrder', {
        value: bookingValue, // number. Value of the order or items sold. Example: 100.
        currency: transactionProps?.currency, // string. The 4217 currency code. Example: 'USD'.
        contents: (transactionProps?.items || []).map((item) => ({
          content_id: item.id, // string. ID of the product. Example: '1077218'.
          content_type: 'product', // string. Either product or product_group.
          content_name: item.name, // string. The name of the page or product. Example: 'shirt'.
        })),
        content_type: 'product',
        content_category: bookingType,
        description: transactionProps?.description, // string. The description of the item or page. Example: "light weight cotton".
      });

      trackMpPinterestEvent('Lead', {
        value: bookingValue,
        currency: transactionProps?.currency,
        property: bookingType,
        lead_type: bookingType,
      });
    }

    const placeIndustries = transactionProps?.place?.about?.industries || [];
    const isCarIndustry = placeIndustries.some((industry) => CAR_INDUSTRIES.includes(industry));
    const isHairdresserIndustry = placeIndustries.some((industry) => HAIRDRESSER_INDUSTRIES.includes(industry));
    const trackIndustry = (eventName: string) => {
      trackPageCio(eventName, cioData);
      trackMpAnalyticsEvent(
        `${eventName}${isSistaminutenMarketplaceBooking ? '_sistaminutentider' : isDealBooking ? '_deals' : ''}`,
        {
          booking_source: 'marketplace',
          value: bookingValue,
          currency: transactionProps?.currency,
          transaction_id: booking_id,
        },
      );
      trackMpMetaEvent(eventName.includes('cars') ? 'InitiateCheckout' : 'AddPaymentInfo', {
        value: bookingValue,
        currency: transactionProps?.currency,
        content_name: isSistaminutenMarketplaceBooking
          ? 'BookingSistaminutentider'
          : isDealBooking
          ? 'BookingDeals'
          : 'Booking',
        order_id: booking_id,
      });
    };
    if (isCarIndustry) {
      trackIndustry('cars_booking_success');
    }
    if (isHairdresserIndustry) {
      trackIndustry('hairdresser_booking_success');
    }
  }

  if (transactionProps?.place?.about) {
    salonTrackEventPurchase({ ...transactionProps });
  }
}
